/**
 * Client for the public StarPlan timetable API.
 *
 * Targets `${NEXT_PUBLIC_SPLAN_API_URL}${NEXT_PUBLIC_SPLAN_API_PREFIX}/*` on
 * the main HFU API. The development build defaults to api.dev.hfu.digital.
 * Override via env for another environment.
 */

const SPLAN_API_URL = process.env.NEXT_PUBLIC_SPLAN_API_URL || 'https://api.dev.hfu.digital';
const SPLAN_API_PREFIX = process.env.NEXT_PUBLIC_SPLAN_API_PREFIX || '/v1/starplan';

export interface Program {
    id: string;
    name: string;
    shortName: string;
    degreeType?: string;
    isActive: boolean;
}

export interface Semester {
    id: string;
    programId: string;
    name: string;
    shortName?: string;
    number?: number; // Computed from name if not present
    program?: Program;
}

export interface Course {
    id: string;
    name: string;
    summary?: string;
    description?: string;
    location?: string;
    instructor?: string;
    dayOfWeek?: number;
    startTime?: string;
    endTime?: string;
}

export interface Room {
    id: string;
    name: string;
    building?: string;
    floor?: string;
}

export interface Instructor {
    id: string;
    name: string;
    department?: string;
}

export interface RoomScheduleEvent {
    courseName: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    instructor?: string;
}

export interface RoomScheduleResponse {
    room: Room;
    period: { from: string; to: string };
    courses: Array<{
        id: string;
        summary: string;
        startTime: string;
        endTime: string;
        weekday: number;
        instructor?: { id: string; name: string };
        semester?: { id: string; name: string };
    }>;
}

export interface TeacherCourse {
    id: string;
    name: string;
    code?: string;
    program?: string;
    semester?: number;
    schedules?: {
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        room?: string;
    }[];
}

export interface ProgramCourse {
    id: string;
    name: string;
    code?: string;
    instructor?: string;
    schedules?: {
        dayOfWeek: number;
        startTime: string;
        endTime: string;
        room?: string;
    }[];
}

// For date-based view - individual course occurrences with actual dates
export interface CourseOccurrence {
    id: string;
    name: string;
    instructor?: string;
    room?: string;
    date: string; // ISO date string
    startTime: string; // HH:mm
    endTime: string; // HH:mm
    dayOfWeek: number;
}

function extractSemesterNumber(name: string): number | undefined {
    const match = name.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : undefined;
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await fetch(`${SPLAN_API_URL}${SPLAN_API_PREFIX}${endpoint}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
        },
    });

    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
}

interface PaginatedResponse<T> {
    data: T[];
    meta?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

interface WrappedResponse<T> {
    data: T;
}

interface TeacherCoursesResponse {
    instructor: Instructor;
    data: TeacherCourse[];
    meta?: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
    };
}

export const splanApi = {
    // Programs
    async getPrograms(): Promise<Program[]> {
        // Request large limit to get all programs (default is 50)
        const response = await fetchApi<PaginatedResponse<Program>>('/programs?limit=500');
        return response.data;
    },

    async getProgram(id: string): Promise<Program> {
        const response = await fetchApi<WrappedResponse<Program>>(`/programs/${id}`);
        return response.data;
    },

    // Semesters
    async getSemesters(programId?: string): Promise<Semester[]> {
        // Request large limit to get all semesters (default is 50)
        const params = new URLSearchParams({ limit: '500' });
        if (programId) {
            params.set('programId', programId);
        }
        const response = await fetchApi<PaginatedResponse<Semester>>(`/semesters?${params.toString()}`);
        // Extract semester number from name for each semester
        return response.data.map((semester) => {
            if (!semester.number && semester.name) {
                const match = semester.name.match(/(\d+)/);
                if (match) {
                    semester.number = parseInt(match[1], 10);
                }
            }
            return semester;
        });
    },

    async getSemester(id: string): Promise<Semester> {
        const response = await fetchApi<WrappedResponse<Semester>>(`/semesters/${id}`);
        const semester = response.data;
        // Extract semester number from name (e.g., "Semester 3" -> 3, "3. Semester" -> 3)
        if (!semester.number && semester.name) {
            const match = semester.name.match(/(\d+)/);
            if (match) {
                semester.number = parseInt(match[1], 10);
            }
        }
        return semester;
    },

    // Courses
    async getCourses(params?: {
        semesterId?: string;
        roomId?: string;
        instructorId?: string;
        weekday?: number;
    }): Promise<Course[]> {
        const searchParams = new URLSearchParams();
        if (params?.semesterId) searchParams.set('semesterId', params.semesterId);
        if (params?.roomId) searchParams.set('roomId', params.roomId);
        if (params?.instructorId) searchParams.set('instructorId', params.instructorId);
        if (params?.weekday !== undefined) searchParams.set('weekday', String(params.weekday));

        const query = searchParams.toString();
        const response = await fetchApi<PaginatedResponse<Course>>(`/courses${query ? `?${query}` : ''}`);
        return response.data;
    },

    // Rooms
    async getRooms(): Promise<Room[]> {
        // Request large limit to get all rooms (default is 50)
        const response = await fetchApi<PaginatedResponse<Room>>('/rooms?limit=500');
        return response.data;
    },

    async getRoom(id: string): Promise<Room> {
        const response = await fetchApi<WrappedResponse<Room>>(`/rooms/${id}`);
        return response.data;
    },

    async getRoomSchedule(id: string): Promise<RoomScheduleEvent[]> {
        // Request 6 months of data to show meaningful schedule
        const now = new Date();
        const sixMonthsLater = new Date(now.getTime() + 180 * 24 * 60 * 60 * 1000);
        const response = await fetchApi<WrappedResponse<RoomScheduleResponse>>(
            `/rooms/${id}/schedule?from=${now.toISOString()}&to=${sixMonthsLater.toISOString()}`
        );
        // Transform courses to the expected RoomScheduleEvent format
        return response.data.courses.map((course) => ({
            courseName: course.summary,
            dayOfWeek: course.weekday,
            startTime: new Date(course.startTime).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
            }),
            endTime: new Date(course.endTime).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
            }),
            instructor: course.instructor?.name,
        }));
    },

    async getRoomAvailability(id: string, date?: string): Promise<any> {
        const params = date ? `?date=${date}` : '';
        const response = await fetchApi<WrappedResponse<any>>(`/rooms/${id}/availability${params}`);
        return response.data;
    },

    // Instructors / Teachers
    async getInstructors(): Promise<Instructor[]> {
        // Request large limit to get all instructors (default is 50)
        const response = await fetchApi<PaginatedResponse<Instructor>>('/instructors?limit=500');
        return response.data;
    },

    async getInstructor(id: string): Promise<Instructor> {
        const response = await fetchApi<WrappedResponse<Instructor>>(`/instructors/${id}`);
        return response.data;
    },

    async getTeacher(id: string): Promise<Instructor> {
        const response = await fetchApi<WrappedResponse<Instructor>>(`/instructors/${id}`);
        return response.data;
    },

    async getTeacherCourses(id: string): Promise<TeacherCourse[]> {
        const response = await fetchApi<TeacherCoursesResponse>(`/instructors/${id}/courses?limit=500`);

        // Backend returns flat Course[] with individual time slots
        // We need to group by course summary and aggregate unique schedules
        const courseMap = new Map<string, TeacherCourse & { scheduleKeys: Set<string> }>();

        for (const course of response.data as unknown as Array<{
            id: string;
            summary: string;
            startTime: string;
            endTime: string;
            weekday: number;
            room?: { id: string; name: string };
            semester?: { id: string; name: string; program?: { id: string; name: string } };
        }>) {
            const key = course.summary;

            if (!courseMap.has(key)) {
                courseMap.set(key, {
                    id: course.id,
                    name: course.summary,
                    program: course.semester?.program?.name,
                    semester: course.semester?.name ? extractSemesterNumber(course.semester.name) : undefined,
                    schedules: [],
                    scheduleKeys: new Set(),
                });
            }

            const existing = courseMap.get(key)!;
            const startTime = new Date(course.startTime).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
            });
            const endTime = new Date(course.endTime).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
            });
            const roomName = course.room?.name;

            // Deduplicate: only add if this exact schedule doesn't exist
            const scheduleKey = `${course.weekday}-${startTime}-${endTime}-${roomName || ''}`;
            if (!existing.scheduleKeys.has(scheduleKey)) {
                existing.scheduleKeys.add(scheduleKey);
                existing.schedules?.push({
                    dayOfWeek: course.weekday,
                    startTime,
                    endTime,
                    room: roomName,
                });
            }
        }

        // Remove the internal scheduleKeys before returning
        return Array.from(courseMap.values()).map(({ scheduleKeys, ...course }) => course);
    },

    // Program Courses (uses semesters endpoint, with fallback to programs endpoint for program-as-semester)
    async getProgramCourses(programId: string, semesterId: string): Promise<ProgramCourse[]> {
        const isProgramAsSemester = programId === semesterId;

        // Helper function to transform raw course data
        const transformCourses = (data: Array<{
            id: string;
            summary: string;
            startTime: string;
            endTime: string;
            weekday: number;
            room?: { id: string; name: string; building?: string };
            instructor?: { id: string; name: string };
        }>): ProgramCourse[] => {
            const courseMap = new Map<string, ProgramCourse & { scheduleKeys: Set<string> }>();

            for (const course of data) {
                const key = course.summary;

                if (!courseMap.has(key)) {
                    courseMap.set(key, {
                        id: course.id,
                        name: course.summary,
                        instructor: course.instructor?.name,
                        schedules: [],
                        scheduleKeys: new Set(),
                    });
                }

                const existing = courseMap.get(key)!;
                const startTime = new Date(course.startTime).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                const endTime = new Date(course.endTime).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                });
                const roomName = course.room?.name;

                // Deduplicate: only add if this exact schedule doesn't exist
                const scheduleKey = `${course.weekday}-${startTime}-${endTime}-${roomName || ''}`;
                if (!existing.scheduleKeys.has(scheduleKey)) {
                    existing.scheduleKeys.add(scheduleKey);
                    existing.schedules?.push({
                        dayOfWeek: course.weekday,
                        startTime,
                        endTime,
                        room: roomName,
                    });
                }
            }

            return Array.from(courseMap.values()).map(({ scheduleKeys, ...course }) => course);
        };

        // Try semester endpoint first
        try {
            const response = await fetchApi<PaginatedResponse<unknown>>(`/semesters/${semesterId}/courses?limit=500`);
            if (response.data && response.data.length > 0) {
                return transformCourses(response.data as Array<{
                    id: string;
                    summary: string;
                    startTime: string;
                    endTime: string;
                    weekday: number;
                    room?: { id: string; name: string; building?: string };
                    instructor?: { id: string; name: string };
                }>);
            }
        } catch {
            // Semester endpoint failed, will try program endpoint if applicable
        }

        // If program-as-semester case and semester endpoint returned no courses, try program endpoint
        if (isProgramAsSemester) {
            try {
                const response = await fetchApi<PaginatedResponse<unknown>>(`/programs/${programId}/courses?limit=500`);
                return transformCourses(response.data as Array<{
                    id: string;
                    summary: string;
                    startTime: string;
                    endTime: string;
                    weekday: number;
                    room?: { id: string; name: string; building?: string };
                    instructor?: { id: string; name: string };
                }>);
            } catch {
                // Program endpoint also failed
            }
        }

        return [];
    },

    // Program Course Occurrences - for date-based view (returns individual occurrences with dates)
    async getProgramCourseOccurrences(programId: string, semesterId: string): Promise<CourseOccurrence[]> {
        const isProgramAsSemester = programId === semesterId;

        // Helper to transform raw data to CourseOccurrence[]
        const transformOccurrences = (data: Array<{
            id: string;
            summary: string;
            startTime: string;
            endTime: string;
            weekday: number;
            room?: { id: string; name: string };
            instructor?: { id: string; name: string };
        }>): CourseOccurrence[] => {
            return data.map((course) => {
                const startDate = new Date(course.startTime);
                const endDate = new Date(course.endTime);
                return {
                    id: course.id,
                    name: course.summary,
                    instructor: course.instructor?.name,
                    room: course.room?.name,
                    date: startDate.toISOString().split('T')[0],
                    startTime: startDate.toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    endTime: endDate.toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit',
                    }),
                    dayOfWeek: course.weekday,
                };
            }).sort((a, b) => a.date.localeCompare(b.date) || a.startTime.localeCompare(b.startTime));
        };

        // Try semester endpoint first
        try {
            const response = await fetchApi<PaginatedResponse<unknown>>(`/semesters/${semesterId}/courses?limit=500`);
            if (response.data && response.data.length > 0) {
                return transformOccurrences(response.data as Array<{
                    id: string;
                    summary: string;
                    startTime: string;
                    endTime: string;
                    weekday: number;
                    room?: { id: string; name: string };
                    instructor?: { id: string; name: string };
                }>);
            }
        } catch {
            // Semester endpoint failed, will try program endpoint if applicable
        }

        // If program-as-semester case and semester endpoint returned no courses, try program endpoint
        if (isProgramAsSemester) {
            try {
                const response = await fetchApi<PaginatedResponse<unknown>>(`/programs/${programId}/courses?limit=500`);
                return transformOccurrences(response.data as Array<{
                    id: string;
                    summary: string;
                    startTime: string;
                    endTime: string;
                    weekday: number;
                    room?: { id: string; name: string };
                    instructor?: { id: string; name: string };
                }>);
            } catch {
                // Program endpoint also failed
            }
        }

        return [];
    },

    // Search
    async search(query: string): Promise<any> {
        return fetchApi(`/search?q=${encodeURIComponent(query)}`);
    },
};
